import React, { useState } from "react";
import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Collapse, Divider, IconButton } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import LogoutIcon from "@mui/icons-material/Logout";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PublicIcon from "@mui/icons-material/Public";
import ChatIcon from "@mui/icons-material/Chat";
import ForumIcon from "@mui/icons-material/Forum";
import InstagramIcon from "@mui/icons-material/Instagram";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlockIcon from "@mui/icons-material/Block";
import SettingsIcon from "@mui/icons-material/Settings";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useLocation, useNavigate } from "react-router-dom";
import logoImg from "../assets/images/talktokam.webp";
import { useSidebar } from "../App";

const NavItem = ({ item, collapsed, depth }) => (
  <ListItemButton
    onClick={item.onClick}
    selected={item.active}
    sx={{
      borderRadius: 2, mb: 0.3, mx: 0.5, pl: depth ? 4 : 2, minHeight: 40,
      "&.Mui-selected": { bgcolor: "#6366f118" },
      "&:hover": { bgcolor: "#f1f5f9" },
      justifyContent: collapsed ? "center" : "flex-start",
    }}
  >
    {item.icon && (
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: item.active ? "#6366f1" : "#64748b", justifyContent: "center" }}>
        {item.icon}
      </ListItemIcon>
    )}
    {!collapsed && (
      <ListItemText
        primary={item.text}
        sx={{ "& .MuiListItemText-primary": { fontSize: 13, fontWeight: item.active ? 700 : 500, color: item.active ? "#1e293b" : "#64748b" } }}
      />
    )}
    {!collapsed && item.expandIcon}
  </ListItemButton>
);

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed, setCollapsed } = useSidebar();

  const [expandedSections, setExpandedSections] = useState({
    website: ["/website", "/website/conversations"].some(p => location.pathname.startsWith(p)),
    whatsapp: ["/whatsapp", "/whatsapp/conversations"].some(p => location.pathname.startsWith(p)),
    instagram: ["/instagram", "/instagram/conversations"].some(p => location.pathname.startsWith(p)),
    ai: ["/ai", "/admin/ai"].some(p => location.pathname.startsWith(p)),
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_user");
    localStorage.removeItem("admin_token");
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path || (path !== "/" && location.pathname.startsWith(path));

  const renderSection = (title, icon, sectionKey, items) => {
    const anyActive = items.some(i => i.active);
    return (
      <>
        <ListItemButton
          onClick={() => toggleSection(sectionKey)}
          sx={{ borderRadius: 2, mb: 0.3, mx: 0.5, minHeight: 40, justifyContent: collapsed ? "center" : "flex-start" }}
        >
          <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: anyActive ? "#6366f1" : "#64748b", justifyContent: "center" }}>
            {icon}
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={title}
              sx={{ "& .MuiListItemText-primary": { fontSize: 13, fontWeight: anyActive ? 700 : 600, color: anyActive ? "#1e293b" : "#64748b" } }}
            />
          )}
          {!collapsed && (expandedSections[sectionKey] ? <ExpandLess sx={{ fontSize: 18, color: "#94a3b8" }} /> : <ExpandMore sx={{ fontSize: 18, color: "#94a3b8" }} />)}
        </ListItemButton>
        {!collapsed && (
          <Collapse in={expandedSections[sectionKey]}>
            {items.map((item) => (
              <NavItem key={item.text} item={item} collapsed={false} depth={1} />
            ))}
          </Collapse>
        )}
      </>
    );
  };

  return (
    <Box className="sidebar-water-bg" sx={{
      width: collapsed ? 72 : 260,
      height: "100vh",
      bgcolor: "#ffffff",
      borderRight: "1px solid #e2e8f0",
      display: "flex",
      flexDirection: "column",
      position: "fixed",
      left: 0,
      top: 0,
      transition: "width 0.25s ease",
      zIndex: 1200,
    }}>
      <Box className="sidebar-content" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ p: collapsed ? 1 : 2, display: "flex", alignItems: "center", gap: 1.2, borderBottom: "1px solid #e2e8f0", justifyContent: collapsed ? "center" : "flex-start" }}>
          <Box component="img" src={logoImg} alt="Talktokam" sx={{ height: collapsed ? 28 : 32, width: "auto", borderRadius: "6px", transition: "height 0.25s" }} />
          {!collapsed && <Typography fontWeight={800} fontSize={16} color="#1e293b">Talktokam</Typography>}
        </Box>

        <List sx={{ flex: 1, px: 1, py: 1, overflow: "auto" }}>
          <NavItem
            collapsed={collapsed}
            item={{ text: "Dashboard", icon: <DashboardIcon sx={{ fontSize: 20 }} />, path: "/dashboard", active: location.pathname === "/dashboard", onClick: () => navigate("/dashboard") }}
          />
          <NavItem
            collapsed={collapsed}
            item={{ text: "Analytics", icon: <AnalyticsIcon sx={{ fontSize: 20 }} />, path: "/analytics", active: location.pathname === "/analytics", onClick: () => navigate("/analytics") }}
          />

          <Divider sx={{ my: 1, borderColor: "#e2e8f0" }} />

          {renderSection("Website", <PublicIcon sx={{ fontSize: 20 }} />, "website", [
            { text: "Overview", icon: <PublicIcon sx={{ fontSize: 18 }} />, path: "/website", active: location.pathname === "/website", onClick: () => navigate("/website") },
            { text: "Conversations", icon: <ChatIcon sx={{ fontSize: 18 }} />, path: "/website/conversations", active: location.pathname === "/website/conversations", onClick: () => navigate("/website/conversations") },
          ])}

          {renderSection("WhatsApp", <ForumIcon sx={{ fontSize: 20 }} />, "whatsapp", [
            { text: "Clients", icon: <ForumIcon sx={{ fontSize: 18 }} />, path: "/whatsapp", active: location.pathname === "/whatsapp", onClick: () => navigate("/whatsapp") },
            { text: "Conversations", icon: <ChatIcon sx={{ fontSize: 18 }} />, path: "/whatsapp/conversations", active: location.pathname === "/whatsapp/conversations", onClick: () => navigate("/whatsapp/conversations") },
            { text: "Recently Suspended", icon: <BlockIcon sx={{ fontSize: 18 }} />, path: "/whatsapp/suspended", active: location.pathname === "/whatsapp/suspended", onClick: () => navigate("/whatsapp/suspended") },
          ])}

          {renderSection("Instagram", <InstagramIcon sx={{ fontSize: 20 }} />, "instagram", [
            { text: "Overview", icon: <InstagramIcon sx={{ fontSize: 18 }} />, path: "/instagram", active: location.pathname === "/instagram", onClick: () => navigate("/instagram") },
            { text: "Conversations", icon: <ChatIcon sx={{ fontSize: 18 }} />, path: "/instagram/conversations", active: location.pathname === "/instagram/conversations", onClick: () => navigate("/instagram/conversations") },
          ])}

          {renderSection("AI Agents", <AutoAwesomeIcon sx={{ fontSize: 20 }} />, "ai", [
            { text: "Agents", icon: <AutoAwesomeIcon sx={{ fontSize: 18 }} />, path: "/ai", active: location.pathname.startsWith("/ai"), onClick: () => navigate("/ai") },
          ])}

          <Divider sx={{ my: 1, borderColor: "#e2e8f0" }} />

          <NavItem
            collapsed={collapsed}
            item={{ text: "Users", icon: <PeopleIcon sx={{ fontSize: 20 }} />, path: "/users", active: location.pathname === "/users", onClick: () => navigate("/users") }}
          />
          <NavItem
            collapsed={collapsed}
            item={{ text: "Bots", icon: <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />, path: "/bots", active: location.pathname === "/bots", onClick: () => navigate("/bots") }}
          />
          <NavItem
            collapsed={collapsed}
            item={{ text: "Plans", icon: <WorkspacePremiumIcon sx={{ fontSize: 20 }} />, path: "/plans", active: location.pathname === "/plans", onClick: () => navigate("/plans") }}
          />
          <NavItem
            collapsed={collapsed}
            item={{ text: "Earnings", icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />, path: "/earnings", active: location.pathname === "/earnings", onClick: () => navigate("/earnings") }}
          />
          <NavItem
            collapsed={collapsed}
            item={{ text: "Settings", icon: <SettingsIcon sx={{ fontSize: 20 }} />, path: "/settings", active: location.pathname === "/settings", onClick: () => navigate("/settings") }}
          />
        </List>

        <Divider sx={{ borderColor: "#e2e8f0" }} />
        <List sx={{ px: 1, py: 1 }}>
          <ListItemButton onClick={() => setCollapsed(!collapsed)} sx={{ borderRadius: 2, mx: 0.5, mb: 0.3, "&:hover": { bgcolor: "#f1f5f9" }, justifyContent: collapsed ? "center" : "flex-start" }}>
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: "#64748b", justifyContent: "center" }}>
              {collapsed ? <MenuIcon sx={{ fontSize: 20 }} /> : <MenuOpenIcon sx={{ fontSize: 20 }} />}
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Collapse" sx={{ "& .MuiListItemText-primary": { fontSize: 13, fontWeight: 500, color: "#64748b" } }} />}
          </ListItemButton>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, mx: 0.5, "&:hover": { bgcolor: "#fef2f2" }, justifyContent: collapsed ? "center" : "flex-start" }}>
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: "#ef4444", justifyContent: "center" }}><LogoutIcon sx={{ fontSize: 20 }} /></ListItemIcon>
            {!collapsed && <ListItemText primary="Logout" sx={{ "& .MuiListItemText-primary": { fontSize: 13, fontWeight: 500, color: "#ef4444" } }} />}
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );
};

export default Sidebar;
